import { useCallback, useEffect, useRef, useState } from 'react';
import type { AddressSuggestion, OrderLocation } from '../../types';

/** Data source for address suggestions; each app binds its own API client. */
export interface AddressAutocompleteSource {
  fetchSuggestions(query: string, lang?: string): Promise<AddressSuggestion[]>;
  resolveLocation(suggestion: AddressSuggestion): Promise<OrderLocation | null>;
}

export interface UseAddressAutocompleteOptions {
  source: AddressAutocompleteSource;
  minLength?: number;
  debounceMs?: number;
  lang?: string | undefined;
  /** Called when the user picks a suggestion (resolved location) or edits the text (`null`). */
  onLocationChange?: ((location: OrderLocation | null) => void) | undefined;
  onSuggestionChange?: ((suggestion: AddressSuggestion | null) => void) | undefined;
}

export interface UseAddressAutocompleteResult {
  suggestions: AddressSuggestion[];
  isLoading: boolean;
  showSuggestions: boolean;
  onInputChange: (value: string) => void;
  onSuggestionSelect: (suggestion: AddressSuggestion) => void;
  dismissSuggestions: () => void;
}

export function useAddressAutocomplete(
  currentValue: string,
  setValue: (value: string) => void,
  options: UseAddressAutocompleteOptions,
): UseAddressAutocompleteResult {
  const {
    source,
    minLength = 3,
    debounceMs = 300,
    lang,
    onLocationChange,
    onSuggestionChange,
  } = options;

  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const skipNextFetchRef = useRef(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    const normalizedValue = currentValue.trim();
    if (normalizedValue.length < minLength) {
      return;
    }

    const timer = setTimeout(() => {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);

      source
        .fetchSuggestions(normalizedValue, lang)
        .then((results) => {
          if (requestId === requestIdRef.current) {
            setSuggestions(results);
          }
        })
        .catch(() => {
          if (requestId === requestIdRef.current) {
            setSuggestions([]);
          }
        })
        .finally(() => {
          if (requestId === requestIdRef.current) {
            setIsLoading(false);
          }
        });
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [currentValue, minLength, debounceMs, lang, source]);

  const onInputChange = useCallback(
    (value: string) => {
      requestIdRef.current += 1;
      setSuggestions([]);
      setIsLoading(false);
      setIsDismissed(false);
      setValue(value);
      onLocationChange?.(null);
      onSuggestionChange?.(null);
    },
    [setValue, onLocationChange, onSuggestionChange],
  );

  const onSuggestionSelect = useCallback(
    (suggestion: AddressSuggestion) => {
      const requestId = ++requestIdRef.current;
      skipNextFetchRef.current = true;
      setValue(suggestion.formatted);
      setSuggestions([]);
      setIsDismissed(true);
      setIsLoading(true);

      source
        .resolveLocation(suggestion)
        .then((location) => {
          if (requestId !== requestIdRef.current) {
            return;
          }
          onSuggestionChange?.(location ? suggestion : null);
          onLocationChange?.(location);
        })
        .catch(() => {
          if (requestId !== requestIdRef.current) {
            return;
          }
          onSuggestionChange?.(null);
          onLocationChange?.(null);
        })
        .finally(() => {
          if (requestId === requestIdRef.current) {
            setIsLoading(false);
          }
        });
    },
    [setValue, source, onLocationChange, onSuggestionChange],
  );

  const dismissSuggestions = useCallback(() => {
    setIsDismissed(true);
  }, []);

  const showSuggestions =
    !isDismissed && currentValue.trim().length >= minLength && suggestions.length > 0;

  return {
    suggestions,
    isLoading,
    showSuggestions,
    onInputChange,
    onSuggestionSelect,
    dismissSuggestions,
  };
}
